import { useCallback, useState } from 'react';

import {
  AllUsersSegmentID,
  FieldDisplayMode,
  IdeaFieldTypeConfig,
  IdeaFieldTypes,
  MaxFieldOptionCharLength,
  MaxFieldOptions,
} from 'common/constants/ideas';
import { IdeaScoreFieldTypes } from 'common/constants/ideaScoreFields';
import { CalculationFunctions } from 'common/prioritization/CalculationFactorTypes';

import type { CustomIdeaField, FormulaIdeaField, ScoreIdeaField } from 'common/api/endpoints/idea';
import type { CustomIdeaFieldTypes } from 'common/types/ideas';

type FieldType = CustomIdeaFieldTypes | IdeaFieldTypes.formula | IdeaFieldTypes.score;

export type InitialFormData = CustomIdeaField | FormulaIdeaField | ScoreIdeaField;

type BaseFieldFormState = {
  name: string;
};

type InitialFieldFormState = BaseFieldFormState & {
  type: null;
};

export type CustomFieldFormState = BaseFieldFormState &
  Pick<CustomIdeaField, 'type' | 'options' | 'displayMode'>;

export type FormulaFieldFormState = BaseFieldFormState & {
  type: IdeaFieldTypes.formula;
  segmentID: NonNullable<FormulaIdeaField['segmentID']>;
  factor: FormulaIdeaField['factor'];
  fieldID: FormulaIdeaField['fieldID'];
  calculationFunction: FormulaIdeaField['calculationFunction'] | null;
  displayMode?: FieldDisplayMode;
};

export type ScoreFieldFormState = BaseFieldFormState & {
  type: IdeaFieldTypes.score;
  fields: ScoreIdeaField['fields'];
  displayMode?: FieldDisplayMode;
};

export type FieldFormState =
  | InitialFieldFormState
  | CustomFieldFormState
  | FormulaFieldFormState
  | ScoreFieldFormState;

// All possible field names from any state variant
type AllFieldNames = keyof (InitialFieldFormState &
  CustomFieldFormState &
  FormulaFieldFormState &
  ScoreFieldFormState);

export type FormulaChangeFields = Exclude<keyof FormulaFieldFormState, 'name' | 'type'>;
export type ScoreChangeFields = Exclude<keyof ScoreFieldFormState, 'name' | 'type'>;
export type CustomFieldSubmission = CustomFieldFormState;
export type FormulaFieldSubmission = FormulaFieldFormState & {
  calculationFunction: CalculationFunctions;
};
export type ScoreFieldSubmission = ScoreFieldFormState;
export type FieldSubmission = CustomFieldSubmission | FormulaFieldSubmission | ScoreFieldSubmission;

type ValidationResult =
  | {
      isValid: true;
      submission: FieldSubmission;
    }
  | {
      isValid: false;
      submission: null;
    };

// Define the interface for field type configurations
interface FieldConfig<T extends FieldFormState = FieldFormState> {
  defaultState: (initialData: any) => T;
  setValue: <K extends AllFieldNames, V>(currentState: T, field: K, value: V) => FieldFormState;
  validate: (state: T) => ValidationResult;
}

const getResetState = (
  selectedType: IdeaFieldTypes | null,
  existingState: FieldFormState,
): FieldFormState => {
  switch (selectedType) {
    case null:
      return { name: existingState.name, type: null };
    case IdeaFieldTypes.formula:
      return {
        name: existingState.name,
        type: IdeaFieldTypes.formula,
        segmentID: AllUsersSegmentID,
        factor: null,
        fieldID: null,
        calculationFunction: CalculationFunctions.sum,
        displayMode: FieldDisplayMode.standard,
      };
    case IdeaFieldTypes.score:
      return {
        name: existingState.name,
        type: IdeaFieldTypes.score,
        fields: [],
        displayMode: FieldDisplayMode.standard,
      };
    case IdeaFieldTypes.number:
      return {
        name: existingState.name,
        type: IdeaFieldTypes.number,
        options: [''],
        displayMode: FieldDisplayMode.standard,
      };
    case IdeaFieldTypes.range:
      return {
        name: existingState.name,
        type: IdeaFieldTypes.range,
        options: ['', ''],
        displayMode: undefined,
      };
    default:
      return {
        name: existingState.name,
        type: selectedType,
        options: [''],
        displayMode: undefined,
      };
  }
};

const getFieldConfig = (type: FieldType | null): FieldConfig => {
  switch (type) {
    case null:
      return {
        defaultState: () => ({
          name: '',
          type: null,
        }),
        setValue: (currentState, field, value) => {
          if (field === 'name') {
            return { ...currentState, name: value as string };
          }
          return currentState;
        },
        validate: () => ({ isValid: false, submission: null }),
      };
    case IdeaFieldTypes.score:
      return {
        defaultState: (data: ScoreIdeaField) => {
          const { label, fields, ...rest } = data;
          return {
            ...rest,
            fields: fields ?? [],
            name: label ?? '',
            type: IdeaFieldTypes.score,
            displayMode: data.displayMode,
          };
        },
        setValue: (currentState, field, value) => {
          if (field === 'name') {
            return { ...currentState, name: value as string };
          }

          if (field === 'fields') {
            return { ...currentState, fields: value as ScoreIdeaField['fields'] };
          }

          if (field === 'displayMode') {
            return { ...currentState, displayMode: value as FieldDisplayMode };
          }

          return currentState;
        },
        validate: (state) => {
          const { fields, name } = state as ScoreFieldFormState;

          const isFieldValid = (field: ScoreIdeaField['fields'][number]) => {
            const weightIsValid = typeof field.weight === 'number';
            const fieldIDisValid = typeof field.fieldID === 'string';
            const typeIsValid = field.type in IdeaScoreFieldTypes;
            return weightIsValid && fieldIDisValid && typeIsValid;
          };

          if (fields.length === 0) {
            return { isValid: false, submission: null };
          } else if (!name) {
            return { isValid: false, submission: null };
          } else if (fields.some((field) => !isFieldValid(field))) {
            return { isValid: false, submission: null };
          }

          return {
            isValid: true,
            submission: state as ScoreFieldSubmission,
          };
        },
      };
    case IdeaFieldTypes.formula:
      return {
        defaultState: (data: FormulaIdeaField) => {
          const { segmentID, label, ...rest } = data;
          return {
            ...rest,
            segmentID: segmentID ?? AllUsersSegmentID,
            name: label,
            type: IdeaFieldTypes.formula,
            displayMode: data.displayMode,
          };
        },
        setValue: (currentState, field, value) => {
          if (field === 'name') {
            return { ...currentState, name: value as string };
          }

          if (field === 'factor' && value) {
            return { ...currentState, factor: value as string, fieldID: null };
          }

          if (field === 'fieldID' && value) {
            return { ...currentState, fieldID: value as string, factor: null };
          }

          if (field === 'segmentID' || field === 'calculationFunction') {
            return { ...currentState, [field]: value };
          }

          if (field === 'displayMode') {
            return { ...currentState, displayMode: value as FieldDisplayMode };
          }

          return currentState;
        },
        validate: (state) => {
          const { calculationFunction, factor, fieldID } = state as FormulaFieldFormState;

          if (!calculationFunction) {
            return { isValid: false, submission: null };
          }

          // Check that exactly one of factor or fieldID is set
          if (Boolean(factor) === Boolean(fieldID)) {
            return {
              isValid: false,
              submission: null,
            };
          }

          return {
            isValid: true,
            submission: {
              ...state,
              calculationFunction,
              factor,
              fieldID,
              displayMode: (state as FormulaFieldFormState).displayMode,
            } as FormulaFieldSubmission,
          };
        },
      };
    default:
      return {
        defaultState: (data: CustomIdeaField) => ({
          name: data.label,
          type: data.type,
          options: data.options ?? [''],
          displayMode: data.displayMode,
        }),
        setValue: (currentState, field, value) => {
          if (field === 'name') {
            return { ...currentState, name: value as string };
          }

          if (field === 'options') {
            const incomingOptions = value as string[];

            if (incomingOptions.length >= MaxFieldOptions) {
              return currentState;
            }

            return {
              ...currentState,
              options: incomingOptions,
            };
          }

          if (field === 'displayMode') {
            return { ...currentState, displayMode: value as FieldDisplayMode };
          }

          return currentState;
        },
        validate: (state) => {
          const customState = state as CustomFieldFormState;
          const { hasOptions } = IdeaFieldTypeConfig[customState.type];
          const hasDisplayMode = customState.type === IdeaFieldTypes.number;

          if (!hasOptions) {
            return {
              isValid: true,
              submission: {
                name: customState.name,
                type: customState.type,
                ...(hasDisplayMode ? { displayMode: customState.displayMode } : {}),
              } as CustomFieldSubmission,
            };
          }

          const { options = [] } = customState;
          if (options.length === 0) {
            return { isValid: false, submission: null };
          }

          const trimmedOptions = options.map((opt) => opt.trim());
          let allValid = trimmedOptions.every(
            (opt) => opt.length > 0 && opt.length <= MaxFieldOptionCharLength,
          );

          if (customState.type === IdeaFieldTypes.range) {
            // For range, require exactly two integer strings and min < max
            if (trimmedOptions.length !== 2) {
              return { isValid: false, submission: null };
            }
            const [minStr, maxStr] = trimmedOptions;
            const min = Number(minStr);
            const max = Number(maxStr);
            const bothIntegers = Number.isInteger(min) && Number.isInteger(max);
            const increasing = bothIntegers && min < max;
            allValid = allValid && increasing;
          }

          if (!allValid) {
            return { isValid: false, submission: null };
          }

          return {
            isValid: true,
            submission: {
              name: customState.name,
              type: customState.type,
              options: trimmedOptions,
              ...(hasDisplayMode ? { displayMode: customState.displayMode } : {}),
            } as CustomFieldSubmission,
          };
        },
      };
  }
};

export function useIdeaFieldForm(initialData?: InitialFormData) {
  const [formState, setFormState] = useState<FieldFormState>(() => {
    const fieldType = initialData?.type ?? null;
    const fieldConfig = getFieldConfig(fieldType);
    return fieldConfig.defaultState(initialData);
  });

  const { setValue: _setValue, validate } = getFieldConfig(formState.type);

  const setValue = useCallback(
    <K extends AllFieldNames, V>(field: K, value: V) => {
      setFormState((currentState) => {
        // Type changes require special handling to reset the state
        if (field === 'type') {
          return getResetState(value as FieldType | null, currentState);
        }

        return _setValue(currentState, field, value);
      });
    },
    [_setValue],
  );

  const validateForm: () => ValidationResult = useCallback(() => {
    if (!formState.name || !formState.type) {
      return { isValid: false, submission: null };
    }

    return validate(formState);
  }, [formState, validate]);

  const { isValid, submission } = validateForm();

  const handleSubmit = useCallback(
    (onSubmit: (data: FieldSubmission) => void) => {
      if (!isValid) {
        return;
      }

      onSubmit(submission);
    },
    [isValid, submission],
  );

  return {
    formState,
    isValid,
    setValue,
    handleSubmit,
  };
}
